// PlaybooksPanel — Harvi's playbooks and whether each is being used
// (Settings → AI Connections). READ-ONLY.
//
// Playbooks are files in the repo (api/src/ai/playbooks), reviewed and
// deployed like any other change, so there is deliberately no editor here.
// What this panel adds is the one thing the files cannot say: how often Harvi
// actually loads each one. The counts and the note come from the server
// (/assistant/playbook-usage); the panel only formats them.
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { fmt } from '../../utils/format';
import { Spinner, ErrorMsg, Card } from '../ui';

function UsageLine({ usage, days }) {
  if (!usage) return null;
  const od = usage.onDemand || {};
  const parts = [];
  parts.push(od.loads
    ? `Loaded in chats ${od.loads}× by ${od.users} ${od.users === 1 ? 'person' : 'people'}${od.perHundredTurns != null ? ` (${od.perHundredTurns} per 100 Harvi turns)` : ''}, last ${fmt.date(od.lastUTC)}`
    : `Not loaded in a chat in the last ${days} days`);
  for (const b of usage.builtIn || []) parts.push(`${b.label} ${b.loads}×`);
  return (
    <div style={{ fontSize: 12, marginTop: 2, color: usage.unused ? B.amber : B.textLt }}>
      {parts.join(' · ')}
      {usage.unused && usage.note && <div style={{ color: B.textMd, marginTop: 2 }}>{usage.note}</div>}
    </div>
  );
}

export function PlaybooksPanel() {
  const { data, loading, error } = useApi(() => api.get('assistant/playbook-usage?days=30'), []);
  const days = data?.days || 30;
  return (
    <Card style={{ padding: 16 }}>
      <div style={{ fontWeight: 600, color: B.text, marginBottom: 4 }}>Playbooks</div>
      <div style={{ fontSize: 13, color: B.textMd, marginBottom: 10 }}>
        Harvi's playbooks live in the code repository under <code>api/src/ai/playbooks</code>. To add or
        change one, edit its file there; the change is reviewed and deployed like any other.
      </div>
      {loading && <Spinner />}
      {error && <ErrorMsg message={error} />}
      {data && data.available === false && data.reason && (
        <div style={{ fontSize: 12, color: B.textLt, marginBottom: 6 }}>{data.reason}</div>
      )}
      {(data?.playbooks || []).map(p => (
        <div key={p.name} style={{ padding: '6px 0', borderTop: `1px solid ${B.divider}` }}>
          <div style={{ fontSize: 13, color: B.text, fontWeight: 500 }}>{p.name}</div>
          <UsageLine usage={p} days={days} />
        </div>
      ))}
    </Card>
  );
}
